import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { formatPrice } from '@/lib/formatPrice'
import { PATHS } from '@/router/paths'
import { useLocationRef } from '@/contexts/RefContext'
import type { MenuItem as MenuItemData } from '@/shared/api/menu'
import {
	AddButton,
	CardBody,
	CardDescription,
	CardImage,
	CardImagePlaceholder,
	CardImageWrap,
	CardInfo,
	CardName,
	CardPrice,
	CardPriceRow,
	CardRoot,
	SoldOutLabel,
	StockBadge,
} from './MenuItem.styled'

interface MenuItemProps {
	item: MenuItemData
	currency?: string
}

export default function MenuItem({ item, currency = 'USD' }: MenuItemProps) {
	const { t } = useFormatMessage()
	const intl = useIntl()
	const navigate = useNavigate()
	const locationRef = useLocationRef()

	const isOutOfStock = item.stockStatus === 'OUT_OF_STOCK'
	const isLowStock = item.stockStatus === 'LOW_STOCK'

	const price = formatPrice(item.onlinePrice ?? item.price, intl.locale, currency)

	return (
		<CardRoot
			isOutOfStock={isOutOfStock}
			aria-disabled={isOutOfStock || undefined}
			data-testid="menu-item"
		>
			<CardBody>
				<CardInfo>
					<CardName isOutOfStock={isOutOfStock}>{item.name}</CardName>
					{item.description && (
						<CardDescription isOutOfStock={isOutOfStock}>
							{item.description}
						</CardDescription>
					)}
				</CardInfo>

				<CardPriceRow>
					<CardPrice>{price}</CardPrice>
					{isOutOfStock && (
						<SoldOutLabel>{t('component:menuItem:soldOut:label')}</SoldOutLabel>
					)}
				</CardPriceRow>
			</CardBody>

			<CardImageWrap>
				{item.imageUrl800x800 ? (
					<CardImage
						src={item.imageUrl800x800}
						alt=""
						loading="lazy"
						width={144}
						height={144}
					/>
				) : (
					<CardImagePlaceholder aria-hidden="true" />
				)}

				{isLowStock && <StockBadge>{t('component:menuItem:lowStock:badge')}</StockBadge>}

				<AddButton
					isOutOfStock={isOutOfStock}
					aria-label={t('component:menuItem:addItem:label', { name: item.name })}
					aria-hidden={isOutOfStock || undefined}
					tabIndex={isOutOfStock ? -1 : undefined}
					onClick={() => {
						void navigate(PATHS.addItem(locationRef, item.ref))
					}}
				>
					+
				</AddButton>
			</CardImageWrap>
		</CardRoot>
	)
}
